import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import MovieCard from './components/MovieCard';
import CategoryGrid from './components/CategoryGrid';
import TrendingList from './components/TrendingList';
import FeatureHighlights from './components/FeatureHighlights';
import { movies } from '@/mocks/movies';
import { Link } from 'react-router-dom';

export default function Home() {
  const hotMovies = movies.filter((m) => m.isHot).slice(0, 6);
  const newMovies = movies.filter((m) => m.isNew).slice(0, 6);

  useEffect(() => {
    // JSON-LD structured data
    const ldJson = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: '夜猫影院',
          url: 'https://www.yemaocinema.com',
          description: '熬夜追剧首选夜间影视平台，汇聚海量电视剧、电影，深夜看电影、通宵追剧一站搞定。',
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '400-888-夜猫',
            contactType: 'customer service',
            email: 'contact@yemaocinema.com',
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: '北京',
            addressRegion: '朝阳区',
            addressCountry: 'CN',
          },
        },
        {
          '@type': 'WebSite',
          name: '夜猫影院',
          url: 'https://www.yemaocinema.com',
          description: '熬夜追剧首选夜间影视平台',
          inLanguage: 'zh-CN',
        },
        {
          '@type': 'WebPage',
          name: '夜猫影院 - 首页',
          url: 'https://www.yemaocinema.com/',
          isPartOf: { '@id': 'https://www.yemaocinema.com/#website' },
          about: { '@id': 'https://www.yemaocinema.com/#organization' },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: movies[0]?.poster || '',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.yemaocinema.com/',
            },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: '夜猫影院是什么？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '夜猫影院是一个专为夜猫子打造的夜间影视平台，提供海量高清影视内容免费在线观看，支持熬夜追剧和通宵看电影。',
              },
            },
            {
              '@type': 'Question',
              name: '夜猫影院需要付费吗？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '夜猫影院完全免费，所有影视内容均可免费在线观看，无需注册会员，打开即可享受高清观影体验。',
              },
            },
            {
              '@type': 'Question',
              name: '夜猫影院支持哪些设备？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '夜猫影院支持手机、平板、电脑等多端播放，只要设备能上网，随时随地都能在线观看。',
              },
            },
            {
              '@type': 'Question',
              name: '夜猫影院的画质怎么样？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '夜猫影院提供1080P高清和4K超清画质，采用智能CDN加速技术确保流畅不卡顿，给你影院级的观影体验。',
              },
            },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ldJson);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <HeroSection />

        {/* Category Grid */}
        <CategoryGrid />

        {/* Hot Recommendations + Trending */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Hot Movies Grid */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-fire-line text-accent-500"></i>
                    </div>
                    热播推荐
                  </h2>
                  <Link
                    to="/category/all"
                    className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看全部 <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {hotMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>

              {/* Trending Sidebar */}
              <div className="lg:w-80 flex-shrink-0">
                <TrendingList />
              </div>
            </div>
          </div>
        </section>

        {/* New Movies */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-flashlight-line text-primary-500"></i>
                </div>
                最新上线
              </h2>
              <Link
                to="/category/all"
                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} size="small" />
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlights */}
        <FeatureHighlights />
      </main>
      <Footer />
    </>
  );
}